import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, Trash2, Plus, CheckCircle2, Clock, Info, UserCheck, Search } from "lucide-react";

const PLANS = ["free", "pro", "business"];

export default function AdminLifetimeGrants() {
  const { refreshMe } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("business");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // User search + impersonate widget
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/lifetime-grants");
      setRows(data.grants || []);
      setForbidden(false);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/lifetime-grants", {
        email: email.trim().toLowerCase(),
        plan,
        note: note.trim() || undefined,
      });
      toast.success(data.applied_immediately
        ? `Applied ${plan} lifetime to ${data.email} — user already signed up.`
        : `${plan} lifetime queued for ${data.email} — activates on first login.`);
      setEmail(""); setNote(""); setPlan("business");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const revoke = async (targetEmail) => {
    if (!window.confirm(`Revoke lifetime plan for ${targetEmail}? User will be downgraded to Free if signed up.`)) return;
    try {
      await api.delete(`/admin/lifetime-grants/${encodeURIComponent(targetEmail)}`);
      toast.success(`Revoked ${targetEmail}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  // Debounced search for users (min 2 chars)
  useEffect(() => {
    if (searchQ.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/admin/users/search`, { params: { q: searchQ.trim() } });
        setSearchResults(data.users || []);
      } catch (e) {
        // silently ignore — permission or transient issue
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const impersonate = async (userId, userEmail) => {
    if (!window.confirm(`Impersonate ${userEmail}? You'll see the app as they do. Exit anytime via the banner at the top.`)) return;
    try {
      await api.post(`/admin/impersonate/${userId}`);
      await refreshMe();
      toast.success(`Now viewing as ${userEmail}`);
      window.location.assign("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (forbidden) {
    return (
      <div className="vp-card p-10 text-center">
        <Info className="w-8 h-8 text-[#B00020] mx-auto mb-3" />
        <div className="font-display font-bold text-lg">Founder-only page</div>
        <p className="text-[#4A4D57] mt-1 text-sm">This page is restricted to the platform admin account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 vp-fade-up">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EA580C] flex items-center justify-center shadow-md">
          <Crown className="w-5 h-5 text-white" strokeWidth={1.75} />
        </div>
        <div>
          <div className="vp-label">Founder controls</div>
          <h1 className="font-display text-3xl font-bold">Lifetime plans</h1>
        </div>
      </div>
      <p className="text-[#4A4D57] text-sm max-w-2xl">
        Grant lifetime access to any email. If the user hasn't signed up yet, the grant is queued and applied automatically on their first login (email/password or Google).
      </p>

      {/* Grant form */}
      <form onSubmit={grant} className="vp-card p-5 space-y-4" data-testid="grant-form">
        <div className="font-display font-semibold text-lg">Grant a lifetime plan</div>
        <div className="grid sm:grid-cols-[1fr_140px_auto] gap-3">
          <div>
            <div className="vp-label mb-1.5">Email</div>
            <input required type="email" placeholder="founder@example.com" className="vp-input"
              value={email} onChange={(e) => setEmail(e.target.value)} data-testid="grant-email-input" />
          </div>
          <div>
            <div className="vp-label mb-1.5">Plan</div>
            <select className="vp-input" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="grant-plan-select">
              {PLANS.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={busy || !email.trim()}
              className="vp-btn-primary inline-flex items-center gap-2 w-full sm:w-auto justify-center"
              data-testid="grant-submit">
              <Plus className="w-4 h-4" /> {busy ? "Granting…" : "Grant"}
            </button>
          </div>
        </div>
        <div>
          <div className="vp-label mb-1.5">Note (optional)</div>
          <input placeholder="Reason / relationship / campaign" className="vp-input"
            value={note} onChange={(e) => setNote(e.target.value)} data-testid="grant-note-input" />
        </div>
      </form>

      {/* User search + impersonate widget */}
      <div className="vp-card p-5 space-y-3" data-testid="user-search-widget">
        <div className="font-display font-semibold text-lg flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-[#0B5CFF]" /> Impersonate a user
        </div>
        <p className="text-xs text-[#4A4D57]">Search any signed-up user by email to view the app through their eyes. Great for debugging or paid customer support.</p>
        <div className="relative">
          <Search className="w-4 h-4 text-[#747680] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            className="vp-input pl-10"
            placeholder="Search by email (min 2 chars)…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            data-testid="impersonate-search-input"
          />
        </div>
        {searchQ.trim().length >= 2 && (
          <div className="border border-[#E4E6EC] rounded-lg overflow-hidden max-h-64 overflow-y-auto">
            {searching ? (
              <div className="p-3 text-xs text-[#4A4D57]">Searching…</div>
            ) : searchResults.length === 0 ? (
              <div className="p-3 text-xs text-[#4A4D57]">No users match.</div>
            ) : (
              <div className="divide-y divide-[#E4E6EC]">
                {searchResults.map((u) => (
                  <div key={u.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{u.email}</div>
                      {u.name && <div className="text-xs text-[#747680] truncate">{u.name}</div>}
                    </div>
                    <button
                      onClick={() => impersonate(u.id, u.email)}
                      className="vp-btn-primary text-xs inline-flex items-center gap-1 py-1.5 px-3 flex-shrink-0"
                      data-testid={`impersonate-search-${u.id}`}
                    >
                      <UserCheck className="w-3.5 h-3.5" /> Impersonate
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* List */}
      <div className="vp-card overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E4E6EC] flex items-center justify-between flex-wrap gap-2">
          <div className="font-display font-semibold">Active grants ({rows.length})</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#4A4D57]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#4A4D57]">No lifetime grants yet. Use the form above to create one.</div>
        ) : (
          <div className="divide-y divide-[#E4E6EC]">
            {rows.map((g) => (
              <div key={g.email} className="px-5 py-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`grant-row-${g.email}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold text-sm sm:text-base break-all">{g.email}</div>
                    <span className="vp-chip vp-chip-accent" style={{ textTransform: "capitalize" }}>{g.plan}</span>
                    {g.signed_up ? (
                      <span className="inline-flex items-center gap-1 vp-chip text-[10px]" style={{ color: "#1E8E3E" }}>
                        <CheckCircle2 className="w-3 h-3" /> Signed up
                        {g.workspace_lifetime && " · applied"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 vp-chip text-[10px]" style={{ color: "#B45309" }}>
                        <Clock className="w-3 h-3" /> Awaiting signup
                      </span>
                    )}
                  </div>
                  {g.user_name && <div className="text-xs text-[#4A4D57] mt-1">{g.user_name}</div>}
                  {g.note && <div className="text-xs text-[#747680] mt-1 italic">"{g.note}"</div>}
                  {g.granted_at && (
                    <div className="text-[10px] text-[#747680] mt-1 font-mono">Granted {new Date(g.granted_at).toLocaleString()}</div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {g.signed_up && g.user_id && (
                    <button
                      onClick={() => impersonate(g.user_id, g.email)}
                      className="p-2 text-[#4A4D57] hover:text-[#0B5CFF] hover:bg-[#0B5CFF]/10 rounded-md"
                      title="Impersonate this user"
                      data-testid={`impersonate-grant-${g.email}`}
                    >
                      <UserCheck className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                  <button onClick={() => revoke(g.email)}
                    className="p-2 text-[#4A4D57] hover:text-[#B00020] hover:bg-[#FDE8EA] rounded-md"
                    title="Revoke grant"
                    data-testid={`revoke-${g.email}`}>
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
